import Lexical, {$getRoot, $isParagraphNode, $isTextNode} from "lexical";
import {
    AlignStyleType,
    BoldType,
    ColorStyleType,
    FontStyleType, SHORT_ALIGN_STYLE_TYPE, SHORT_COLOR_STYLE_TYPE,
    SHORT_FONT_STYLE_TYPE, SHORT_SIZE_STYLE_TYPE,
    SizeStyleType
} from "./TextStyleType";
import {LexicalUtil} from "../../utils/LexicalUtil";


export type RootProperties = {
    align: AlignStyleType | null;
}
export type ParagraphProperties = {
    align: AlignStyleType | null;
    text: TextProperties[];
}
export type TextProperties = {
    text: string;
    color: ColorStyleType | null;
    font: FontStyleType | null;
    size: SizeStyleType | null;
    weight: BoldType;
}

type SimpleTypeStructure = {
    root: { align?: SHORT_ALIGN_STYLE_TYPE },
    paragraph: {
        align?: SHORT_ALIGN_STYLE_TYPE,
        text: {
            text: string,
            font?: SHORT_FONT_STYLE_TYPE,
            size?: SHORT_SIZE_STYLE_TYPE,
            color?: SHORT_COLOR_STYLE_TYPE,
            weight?: 'bold',
        }[]
    }[]
}


export class TextStructure {
    private readonly root: RootProperties;
    private readonly paragraph: ParagraphProperties[];

    constructor(structure: { root: RootProperties, paragraph: ParagraphProperties[] }) {
        this.root = structure.root;
        this.paragraph = structure.paragraph;
    }

    private _cloneRoot(new_props: { align?: AlignStyleType | null }) {
        return {align: new_props.align === undefined ? this.root.align : new_props.align};
    }

    private _cloneParagraph(new_props: { align?: AlignStyleType | null }) {
        return this.paragraph.map((original) => {
            return {
                align: new_props.align === undefined ? original.align : new_props.align,
                text: original.text
            };
        });
    }

    private _convertText(converter: (t: TextProperties) => void) {
        return this.paragraph.map((original) => {

            let prev = undefined;
            const new_text: TextProperties[] = [];
            for (const text of original.text) {
                const current = Object.assign(text);
                converter(current);

                if (prev
                    && prev.font === current.font
                    && prev.size === current.size
                    && prev.color === current.color
                    && prev.weight === current.weight
                ) {
                    prev.text += current.text;
                } else {
                    new_text.push(current);
                    prev = current;
                }
            }


            return {
                align: original.align,
                text: new_text,
            };
        });
    }

    setAlign(align: AlignStyleType) {
        return new TextStructure({
            root: this._cloneRoot({align: align}),
            paragraph: this._cloneParagraph({align: align})
        });
    }

    setFont(font: FontStyleType) {
        return new TextStructure({
            root: this.root,
            paragraph: this._convertText((t: TextProperties) => t.font = font)
        });
    }

    setSize(size: SizeStyleType) {
        return new TextStructure({
            root: this.root,
            paragraph: this._convertText((t: TextProperties) => t.size = size)
        });
    }

    setColor(color: ColorStyleType) {
        return new TextStructure({
            root: this.root,
            paragraph: this._convertText((t: TextProperties) => t.color = color)
        });
    }

    toggleBold() {
        const all_text_is_bold = this.paragraph.every(p => p.text.every(t => t.weight.isBold()));
        const new_weight = all_text_is_bold ? BoldType.normal() : BoldType.bold();

        return new TextStructure({
            root: this.root,
            paragraph: this._convertText((t: TextProperties) => t.weight = new_weight)
        });
    }


    toRawLexicalEditorState(initial_key_number = 1): Lexical.SerializedEditorState {
        const root: Lexical.SerializedRootNode = {
            children: [],
            direction: null,
            format: this.root.align === null ? LexicalUtil.ELEMENT_FORMAT_TYPE_NONE : this.root.align.toLexicalElementFormatType(),
            indent: 0,
            type: "root",
            version: 1
        }

        this.paragraph.forEach(paragraph => {
            const paragraph_node: Lexical.SerializedElementNode = {
                type: "paragraph",
                children: [],
                format: paragraph.align === null ? LexicalUtil.ELEMENT_FORMAT_TYPE_NONE : paragraph.align.toLexicalElementFormatType(),
                direction: null,
                indent: 0,
                version: 1,
            };

            root.children.push(paragraph_node);

            paragraph.text.forEach(text => {
                const text_node = {
                    type: "text",
                    text: text.text,
                    format: text.weight.toLexicalTextNodeFormat(),
                    style: [text.font?.toCssString(), text.size?.toCssString(), text.color?.toCssString()].filter(t => t).join(''),
                    mode: 'normal',
                    detail: 0,
                    version: 1
                }
                paragraph_node.children.push(text_node);
            })
        });

        return {root};
    }

    toJSON(): object {
        const root = {align: this.root.align === null ? null : this.root.align.toShortName()};
        const paragraph = this.paragraph.map(p => {
            return {
                align: p.align === null ? null : p.align.toShortName(),
                text: p.text.map(t => {
                    return {
                        text: t.text,
                        font: t.font === null ? null : t.font.toShortName(),
                        size: t.size === null ? null : t.size.toShortName(),
                        color: t.color === null ? null : t.color.toShortName(),
                        weight: t.weight.toShortName(),
                    }
                })
            }
        });


        return {root, paragraph};
    }

    static fromLexicalEditorState(editor_state: Lexical.EditorState): TextStructure {
        const structure = editor_state.read(() => {
            const root = $getRoot();

            const root_align = AlignStyleType.fromLexicalElementNodeOrDefault(root, null);

            const paragraph: ParagraphProperties[] = root.getChildren().map(paragraph_node => {
                if (!$isParagraphNode(paragraph_node)) {
                    throw new Error('unexpected node');
                }
                const paragraph_align = AlignStyleType.fromLexicalElementNodeOrDefault(paragraph_node, null);

                const text: TextProperties[] = paragraph_node.getChildren().map(text_node => {
                    if (!$isTextNode(text_node)) {
                        throw new Error('unexpected node');
                    }
                    const style_map = LexicalUtil.textNodeStyleToMap(text_node);
                    const font = style_map.get('font-family');
                    const size = style_map.get('font-size');
                    const color = style_map.get('color');

                    return {
                        text: text_node.__text,
                        color: color === undefined ? null : ColorStyleType.fromCssValueOrDefault(color, null),
                        font: font === undefined ? null : FontStyleType.fromCssValueOrDefault(font, null),
                        size: size === undefined ? null : SizeStyleType.fromCssValueOrDefault(size, null),
                        weight: BoldType.fromLexicalTextNode(text_node)
                    };
                });

                return {
                    align: paragraph_align,
                    text: text,
                };
            });

            return {
                root: {align: root_align},
                paragraph: paragraph,
            };
        });

        return new TextStructure(structure);
    }

    static fromOldRawLexicalEditorState(raw: { _nodeMap: any }): TextStructure {
        const map = new Map<string, any>(raw._nodeMap);
        const root = map.get('root');

        if (!Array.isArray(root.__children)) {
            throw new Error('unexpected root.__children');
        }

        const paragraph_properties: ParagraphProperties[] = [];
        for (const key of root.__children) {
            const paragraph_node = map.get(key);
            if (paragraph_node.__type !== 'paragraph') {
                continue;
            }

            if (!Array.isArray(paragraph_node.__children)) {
                throw new Error('unexpected paragraph.__children');
            }

            const text_properties: TextProperties[] = [];
            for (const key of paragraph_node.__children) {
                const text_node = map.get(key);
                if (text_node.__type !== 'text') {
                    continue;
                }

                const style_map = LexicalUtil.textNodeStyleToMap(text_node);
                const font = style_map.get('font-family');
                const size = style_map.get('font-size');
                const color = style_map.get('color');

                text_properties.push({
                    text: text_node.__text,
                    color: color === undefined ? null : ColorStyleType.fromCssValueOrDefault(color, null),
                    font: font === undefined ? null : FontStyleType.fromCssValueOrDefault(font, null),
                    size: size === undefined ? null : SizeStyleType.fromCssValueOrDefault(size, null),
                    weight: BoldType.fromLexicalTextNode(text_node)
                });
            }

            paragraph_properties.push({
                align: AlignStyleType.fromLexicalElementNodeOrDefault(paragraph_node, null),
                text: text_properties,
            });

        }

        return new TextStructure({
            root: {align: AlignStyleType.fromLexicalElementNodeOrDefault(root, null)},
            paragraph: paragraph_properties,
        });
    }

    static fromSimpleObject(obj: SimpleTypeStructure): TextStructure {
        const root: RootProperties = {align: obj.root.align === undefined ? null : AlignStyleType.fromShortName(obj.root.align)};
        const paragraph: ParagraphProperties[] = obj.paragraph.map(p => {
            return {
                align: p.align === undefined ? null : AlignStyleType.fromShortName(p.align),
                text: p.text.map(t => {
                    return {
                        text: t.text,
                        font: t.font === undefined ? null : FontStyleType.fromShortName(t.font),
                        size: t.size === undefined ? null : SizeStyleType.fromShortName(t.size),
                        color: t.color === undefined ? null : ColorStyleType.fromShortName(t.color),
                        weight: BoldType.fromShortName(t.weight),
                    }
                })
            }
        });

        return new TextStructure({root, paragraph});
    }

    static fromJSON(obj: any): TextStructure {
        if (!obj.root || !obj.paragraph) {
            throw new Error('invalid object');
        }

        const root: RootProperties = {align: obj.root.align === null ? null : AlignStyleType.fromShortName(obj.root.align)};
        const paragraph: ParagraphProperties[] = [];
        for (const p of obj.paragraph) {
            const align = p.align === null ? null : AlignStyleType.fromShortName(p.align);
            const text: TextProperties[] = [];
            for (const t of p.text) {
                text.push({
                    text: t.text,
                    font: t.font === null ? null : FontStyleType.fromShortName(t.font),
                    size: t.size === null ? null : SizeStyleType.fromShortName(t.size),
                    color: t.color === null ? null : ColorStyleType.fromShortName(t.color),
                    weight: BoldType.fromShortName(t.weight),
                });
            }
            paragraph.push({align, text});
        }

        return new TextStructure({root, paragraph});
    }

}
